import { FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../core/auth-context";

type LocationState = { from?: { pathname?: string } } | null;
type Mode = "signin" | "register";

const friendlyError = (raw: string) => {
  if (!raw) return "Something went wrong. Try again.";
  if (raw.includes("invalid-credential") || raw.includes("wrong-password"))
    return "That email and password don't match.";
  if (raw.includes("user-not-found"))
    return "No account exists for that email.";
  if (raw.includes("email-already-in-use"))
    return "That email is already registered. Try signing in instead.";
  if (raw.includes("weak-password"))
    return "Password must be at least 6 characters.";
  if (raw.includes("invalid-email"))
    return "That doesn't look like a valid email.";
  return raw.replace(/^Firebase:\s*/, "");
};

const AuthScreen = ({ mode }: { mode: Mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, signIn, register } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectTo =
    (location.state as LocationState)?.from?.pathname || "/";

  useEffect(() => {
    if (!authLoading && user) {
      navigate(redirectTo, { replace: true });
    }
  }, [authLoading, user, navigate, redirectTo]);

  // Reset error when toggling mode
  useEffect(() => {
    setError(null);
  }, [mode]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === "signin") {
        await signIn(email, password);
      } else {
        await register(email, password, displayName || undefined);
      }
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      setError(friendlyError(err?.message ?? ""));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-10">
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.4em] text-accent-400 mb-2">
          {mode === "signin" ? "Welcome back" : "Step inside"}
        </p>
        <h1 className="font-display text-4xl text-white">
          {mode === "signin" ? "Sign in" : "Create an account"}
        </h1>
        <p className="text-ink-400 text-sm mt-3">
          {mode === "signin"
            ? "Sign in to submit a story."
            : "Register to share stories of your own."}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-ink-800/70 border border-ink-700 rounded-lg p-6 space-y-4 shadow-glow"
      >
        {mode === "register" && (
          <div>
            <label className="block text-xs uppercase tracking-wide text-ink-300 mb-1">
              Display name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="What should we call you?"
              className="w-full bg-ink-900 border border-ink-600 focus:border-accent-500 focus:outline-none rounded-md px-3 py-2 text-ink-100 placeholder:text-ink-500"
            />
          </div>
        )}

        <div>
          <label className="block text-xs uppercase tracking-wide text-ink-300 mb-1">
            Email
          </label>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-ink-900 border border-ink-600 focus:border-accent-500 focus:outline-none rounded-md px-3 py-2 text-ink-100"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-ink-300 mb-1">
            Password
          </label>
          <input
            type="password"
            autoComplete={
              mode === "signin" ? "current-password" : "new-password"
            }
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-ink-900 border border-ink-600 focus:border-accent-500 focus:outline-none rounded-md px-3 py-2 text-ink-100"
          />
          {mode === "register" && (
            <p className="text-xs text-ink-500 mt-1">
              At least 6 characters.
            </p>
          )}
        </div>

        {error && (
          <div className="text-sm text-red-300 bg-red-900/30 border border-red-800/50 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-accent-500 hover:bg-accent-400 disabled:opacity-60 text-white font-medium py-2.5 rounded-md tracking-wide"
        >
          {submitting
            ? mode === "signin"
              ? "Signing in…"
              : "Creating account…"
            : mode === "signin"
            ? "Sign in"
            : "Create account"}
        </button>

        <div className="text-center text-sm text-ink-400 pt-2">
          {mode === "signin" ? (
            <>
              New here?{" "}
              <Link to="/register" className="text-accent-400 hover:text-accent-300">
                Create an account
              </Link>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <Link to="/login" className="text-accent-400 hover:text-accent-300">
                Sign in
              </Link>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default AuthScreen;
